import {
  LANDMARK_SIZE_MAX,
  MAP_OBJECT_MIN_PX,
  type MapLandmark,
} from "./mapDocument";
import { landmarkDrawSize, type WorldPoint } from "./mapGeometry";
import { just, nothing, type Maybe } from "./maybe";

/** Native artwork size at Size 1× with no axis scale (world px). */
export function landmarkNativeSize(landmark: MapLandmark): {
  width: number;
  height: number;
} {
  return landmarkDrawSize({ ...landmark, size: 1, scaleX: 1, scaleY: 1 }, 1);
}

/**
 * Clamp resize multipliers so each axis stays between MAP_OBJECT_MIN_PX and
 * LANDMARK_SIZE_MAX × native, relative to the drag-start effective scales.
 */
export function clampResizeFactors(
  sx: number,
  sy: number,
  nativeWidth: number,
  nativeHeight: number,
  rx: number,
  ry: number,
  uniform: boolean,
): { rx: number; ry: number } {
  const safeSx = Math.max(sx, Number.EPSILON);
  const safeSy = Math.max(sy, Number.EPSILON);
  const minSx = MAP_OBJECT_MIN_PX / Math.max(nativeWidth, MAP_OBJECT_MIN_PX);
  const minSy = MAP_OBJECT_MIN_PX / Math.max(nativeHeight, MAP_OBJECT_MIN_PX);
  const maxS = LANDMARK_SIZE_MAX;
  if (uniform) {
    const ratio = Math.min(
      Math.min(maxS / safeSx, maxS / safeSy),
      Math.max(
        Math.max(minSx / safeSx, minSy / safeSy),
        Math.max(Math.abs(rx), Math.abs(ry)),
      ),
    );
    return {
      rx: (rx < 0 ? -1 : 1) * ratio,
      ry: (ry < 0 ? -1 : 1) * ratio,
    };
  }
  let nextRx = rx;
  let nextRy = ry;
  if (Number.isFinite(rx)) {
    nextRx =
      (rx < 0 ? -1 : 1) *
      Math.min(maxS / safeSx, Math.max(minSx / safeSx, Math.abs(rx)));
  }
  if (Number.isFinite(ry)) {
    nextRy =
      (ry < 0 ? -1 : 1) *
      Math.min(maxS / safeSy, Math.max(minSy / safeSy, Math.abs(ry)));
  }
  return { rx: nextRx, ry: nextRy };
}

export const LANDMARK_HANDLES = [
  { id: "nw", x: -1, y: -1 },
  { id: "n", x: 0, y: -1 },
  { id: "ne", x: 1, y: -1 },
  { id: "e", x: 1, y: 0 },
  { id: "se", x: 1, y: 1 },
  { id: "s", x: 0, y: 1 },
  { id: "sw", x: -1, y: 1 },
  { id: "w", x: -1, y: 0 },
] as const;
export type LandmarkHandle = (typeof LANDMARK_HANDLES)[number]["id"] | "rotate";

export function rotatePoint(point: WorldPoint, degrees: number): WorldPoint {
  const radians = (degrees * Math.PI) / 180;
  return {
    x: point.x * Math.cos(radians) - point.y * Math.sin(radians),
    y: point.x * Math.sin(radians) + point.y * Math.cos(radians),
  };
}
export function landmarkLocalPoint(
  landmark: MapLandmark,
  point: WorldPoint,
): WorldPoint {
  return rotatePoint(
    { x: point.x - landmark.x, y: point.y - landmark.y },
    -(landmark.rotation ?? 0),
  );
}

/**
 * Hit-test a landmark mark in world space (rotation + draw size aware).
 * `extraTolerancePx` is converted by zoom (screen pixels → world).
 */
export function hitTestLandmarkMark(
  landmark: MapLandmark,
  world: WorldPoint,
  zoom: number,
  extraTolerancePx = 4,
): boolean {
  const size = landmarkDrawSize(landmark, 1);
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  const tolerance = extraTolerancePx / z;
  const local = landmarkLocalPoint(landmark, world);
  return (
    Math.abs(local.x) <= size.width / 2 + tolerance &&
    Math.abs(local.y) <= size.height / 2 + tolerance
  );
}
export function landmarkWorldPoint(
  landmark: MapLandmark,
  point: WorldPoint,
): WorldPoint {
  const rotated = rotatePoint(point, landmark.rotation ?? 0);
  return { x: landmark.x + rotated.x, y: landmark.y + rotated.y };
}
export function landmarkCorners(landmark: MapLandmark): WorldPoint[] {
  const size = landmarkDrawSize(landmark, 1);
  return LANDMARK_HANDLES.filter(
    (handle) => handle.x !== 0 && handle.y !== 0,
  ).map((handle) =>
    landmarkWorldPoint(landmark, {
      x: (handle.x * size.width) / 2,
      y: (handle.y * size.height) / 2,
    }),
  );
}
export function landmarkHandles(
  landmark: MapLandmark,
  zoom: number,
): { id: LandmarkHandle; point: WorldPoint }[] {
  const size = landmarkDrawSize(landmark, 1);
  return [
    ...LANDMARK_HANDLES.map((handle) => ({
      id: handle.id,
      point: landmarkWorldPoint(landmark, {
        x: (handle.x * size.width) / 2,
        y: (handle.y * size.height) / 2,
      }),
    })),
    {
      id: "rotate",
      point: landmarkWorldPoint(landmark, {
        x: 0,
        y: -size.height / 2 - 26 / zoom,
      }),
    },
  ];
}
export function hitLandmarkHandle(
  landmark: MapLandmark,
  point: WorldPoint,
  zoom: number,
): Maybe<LandmarkHandle> {
  const hit = landmarkHandles(landmark, zoom).find(
    (handle) =>
      Math.hypot(point.x - handle.point.x, point.y - handle.point.y) <=
      8 / zoom,
  );
  return typeof hit === "undefined" ? nothing : just(hit.id);
}

/** Transform from the pointer-down snapshot so repeated pointer events never accumulate error. */
export function transformLandmark(
  start: MapLandmark,
  handle: LandmarkHandle,
  pointer: WorldPoint,
  pointerStart: WorldPoint,
  shift: boolean,
): MapLandmark {
  if (handle === "rotate") {
    const angle =
      ((Math.atan2(pointer.y - start.y, pointer.x - start.x) -
        Math.atan2(pointerStart.y - start.y, pointerStart.x - start.x)) *
        180) /
        Math.PI +
      (start.rotation ?? 0);
    const rotation = shift ? Math.round(angle / 15) * 15 : angle;
    return { ...start, rotation: ((rotation % 360) + 360) % 360 };
  }
  const direction = LANDMARK_HANDLES.find((item) => item.id === handle)!;
  const size = landmarkDrawSize(start, 1);
  const anchor = {
    x: (-direction.x * size.width) / 2,
    y: (-direction.y * size.height) / 2,
  };
  const pointerLocal = landmarkLocalPoint(start, pointer);
  const startLocal = landmarkLocalPoint(start, pointerStart);
  const local = {
    x: (direction.x * size.width) / 2 + pointerLocal.x - startLocal.x,
    y: (direction.y * size.height) / 2 + pointerLocal.y - startLocal.y,
  };
  let rx =
    direction.x === 0 ? 1 : (local.x - anchor.x) / (direction.x * size.width);
  let ry =
    direction.y === 0 ? 1 : (local.y - anchor.y) / (direction.y * size.height);
  const sx = (start.size ?? 1) * (start.scaleX ?? 1);
  const sy = (start.size ?? 1) * (start.scaleY ?? 1);
  const native = landmarkNativeSize(start);
  const clamped = clampResizeFactors(
    sx,
    sy,
    native.width,
    native.height,
    rx,
    ry,
    direction.x !== 0 && direction.y !== 0 && shift === false,
  );
  rx = clamped.rx;
  ry = clamped.ry;
  const center = landmarkWorldPoint(start, {
    x: anchor.x + (direction.x * size.width * rx) / 2,
    y: anchor.y + (direction.y * size.height * ry) / 2,
  });
  return {
    ...start,
    ...center,
    scaleX: (start.scaleX ?? 1) * Math.abs(rx),
    scaleY: (start.scaleY ?? 1) * Math.abs(ry),
    flipX: (start.flipX === true) !== rx < 0,
    flipY: (start.flipY === true) !== ry < 0,
  };
}

export function landmarkHandleCursor(
  handle: LandmarkHandle,
  rotation: number,
): string {
  if (handle === "rotate") return "grab";
  const direction = LANDMARK_HANDLES.find((item) => item.id === handle)!;
  const degrees =
    (Math.atan2(direction.y, direction.x) * 180) / Math.PI + rotation;
  const index = ((Math.round(degrees / 45) % 4) + 4) % 4;
  return ["ew-resize", "nwse-resize", "ns-resize", "nesw-resize"][index]!;
}
